import React, { useEffect, useState } from "react";
import api from "../api/client";
import { PatchPolicy } from "../types";
import ActionButton from "./ActionButton";
import {
  CATEGORY_OPTIONS,
  KbListEditor,
  PolicyChipPicker,
  SEVERITY_OPTIONS,
} from "./PolicyEditors";

/**
 * Global Windows Update approval policy editor. Lives on Devices → Patch
 * Compliance (moved out of Admin → Settings 2026-07-07). Self-contained:
 * loads, edits, and saves /patch-policies/global. Per-customer overrides
 * still live on each customer's page.
 *
 * The save endpoint is admin-only, so render this behind an admin check.
 */
export default function PatchApprovalPolicyCard() {
  const [draft, setDraft] = useState<PatchPolicy | null>(null);
  const [saved, setSaved] = useState(false);

  const load = () =>
    api.get("/api/v1/patch-policies/global").then((r) => setDraft({ ...r.data }));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!draft) return;
    await api.put("/api/v1/patch-policies/global", {
      auto_approve_severities: draft.auto_approve_severities,
      auto_approve_categories: draft.auto_approve_categories,
      decline_severities: draft.decline_severities,
      decline_categories: draft.decline_categories,
      approve_kbs: draft.approve_kbs,
      decline_kbs: draft.decline_kbs,
    });
    setSaved(true);
    load();
    setTimeout(() => setSaved(false), 2000);
  };

  if (!draft) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Patch Approval Policy (Global)</h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Rules that pre-decide which Windows Updates auto-approve, decline, or stay pending review.
        Decline rules win over approve rules. Per-customer overrides live on each customer's page.
      </p>
      <PolicyChipPicker
        label="Auto-approve severities"
        options={SEVERITY_OPTIONS}
        selected={draft.auto_approve_severities}
        onChange={(v) => setDraft({ ...draft, auto_approve_severities: v })}
        color="bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700"
      />
      <PolicyChipPicker
        label="Auto-approve categories"
        options={CATEGORY_OPTIONS}
        selected={draft.auto_approve_categories}
        onChange={(v) => setDraft({ ...draft, auto_approve_categories: v })}
        color="bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700"
      />
      <PolicyChipPicker
        label="Decline severities"
        options={SEVERITY_OPTIONS}
        selected={draft.decline_severities}
        onChange={(v) => setDraft({ ...draft, decline_severities: v })}
        color="bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700"
      />
      <PolicyChipPicker
        label="Decline categories"
        options={CATEGORY_OPTIONS}
        selected={draft.decline_categories}
        onChange={(v) => setDraft({ ...draft, decline_categories: v })}
        color="bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700"
      />
      <KbListEditor
        label="Approve KBs (overrides decline rules)"
        kbs={draft.approve_kbs}
        onChange={(v) => setDraft({ ...draft, approve_kbs: v })}
        color="bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700"
      />
      <KbListEditor
        label="Decline KBs"
        kbs={draft.decline_kbs}
        onChange={(v) => setDraft({ ...draft, decline_kbs: v })}
        color="bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700"
      />
      <div className="flex items-center gap-3 mt-3">
        <ActionButton onAction={() => save()} variant="raw" className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-blue-700" pendingLabel="Saving…">
          Save policy
        </ActionButton>
        {saved && <span className="text-xs text-green-600 dark:text-green-400">Saved — re-applied to all devices</span>}
      </div>
    </div>
  );
}
